import React, { useState } from 'react';
import { Modal, Form, Input, Radio, Slider, Button } from 'antd';
import '../../index.css';

function Survey({ visible, onSubmit, onCancel, suggestedThemes = [], suggestedProjectTitles = [], selectedModule }) {
  const [form] = Form.useForm();
  const [showOther, setShowOther] = useState(false);
  const [showNotFitReason, setShowNotFitReason] = useState(false);
  const isPartiallyWorking = selectedModule === 'Decomposition by Sequence';
  const [emailEntered, setEmailEntered] = useState(false);

  const handleFinish = (values) => {
    delete values.confirmEmail;
    onSubmit(values);
    form.resetFields();
    setShowOther(false);
    setEmailEntered(false);
  };

  const handleQuestion6Change = (e) => {
    const value = e.target.value;
    setShowOther(value === 'other');
    setShowNotFitReason(value === 'no_not_fit');

    // Reset fields when switching
    if (value !== 'other') {
      form.setFieldsValue({ other_reason: '' });
    }
    if (value !== 'no_not_fit') {
      form.setFieldsValue({ not_fit_reason: '' });
    }
  };

  const formatExamples = (examples, isProject = false) => {
    if (!examples || examples.length === 0) return '';
    const displayExamples = isProject
      ? examples.slice(0, 4).map(title => `"${title}"`).join(', ')
      : examples.slice(0, 4).join(', ');
    return ` (e.g., ${displayExamples})`;
  };

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      width={570}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
      >
        {/* Conditionally Render Theme Satisfaction Question */}
        {suggestedThemes.length > 0 && (
          <Form.Item
            name="theme_satisfaction"
            label={
              <strong>
                How would you rate the quality of the <span className="text-danger">theme</span> suggestions
                {/*{formatExamples(suggestedThemes)}*/}
                ?
              </strong>
            }
            rules={[{ required: true, message: 'Please rate the theme suggestions!' }]}
          >
            <Slider
              min={1}
              max={5}
              marks={{
                1: <span className="slider-mark">Very<br />Poor</span>,
                2: <span className="slider-mark">Poor</span>,
                3: <span className="slider-mark">Acceptable</span>,
                4: <span className="slider-mark">Good</span>,
                5: <span className="slider-mark">Very Good</span>,
              }}
              step={1}
            />
          </Form.Item>
        )}

        {/* Project Idea Sastifaction Question */}
        <Form.Item
          name="project_satisfaction"
          label={
            <strong>
              How would you rate the quality of the <span className="text-danger">project idea</span> suggestions
              {/*{formatExamples(suggestedProjectTitles, true)}*/}
              ?
            </strong>
          }
          rules={[{ required: true, message: 'Please rate the project suggestions!' }]}
        >
          <Slider
            min={1}
            max={5}
            marks={{
              1: <span className="slider-mark">Very<br />Poor</span>,
              2: <span className="slider-mark">Poor</span>,
              3: <span className="slider-mark">Acceptable</span>,
              4: <span className="slider-mark">Good</span>,
              5: <span className="slider-mark">Very Good</span>,
            }}
            step={1}
          />
        </Form.Item>

        {/* Image Satisfaction Question*/}
        <Form.Item
          name="image_satisfaction"
          label={<strong>How would you rate the quality of the <span className="text-danger">sprite and backdrop</span> suggestions?</strong>}
          rules={[{ required: true, message: 'Please rate the image suggestions!' }]}
        >
          <Slider
            min={1}
            max={5}
            marks={{
              1: <span className="slider-mark">Very<br />Poor</span>,
              2: <span className="slider-mark">Poor</span>,
              3: <span className="slider-mark">Acceptable</span>,
              4: <span className="slider-mark">Good</span>,
              5: <span className="slider-mark">Very Good</span>,
            }}
            step={1}
          />
        </Form.Item>

        {/* Open-Ended Question */}
        <Form.Item
          name="theme_reason"
          label={<strong>Explain why did you choose this theme for your project?</strong>}
          rules={[{ required: true, message: 'Please provide a reason!' }]}
        >
          <Input.TextArea rows={4} placeholder="Type in here" />
        </Form.Item>

        {/* Multiple-Choice Question with "Other" Option */}
        <Form.Item
          name="classroom_plan"
          label={<strong>Do you plan to use this project in your classroom?</strong>}
          rules={[{ required: true, message: 'Please select an option!' }]}
        >
          <Radio.Group onChange={handleQuestion6Change}>
            <Radio value="yes">Yes</Radio><br />
            <Radio value="no_playing">No, because I was just playing around with the tool</Radio><br />
            <Radio value="no_not_fit">No, because I don’t think this project is a good fit for my classroom</Radio><br />
            <Radio value="other">No, other reason</Radio>
          </Radio.Group>
        </Form.Item>

        {/* Conditional Input for "Other" Option */}
        {showOther && (
          <Form.Item
            name="other_reason"
            label="Please specify your other reason below:"
            rules={[{ required: true, message: 'Please specify your other reason.' }]}
          >
            <Input.TextArea rows={3} placeholder="Type in here" />
          </Form.Item>
        )}

        {/* Conditional Input for "Not Fit" Option */}
        {showNotFitReason && (
          <Form.Item
            name="not_fit_reason"
            label="Why is this project not a good fit for your classroom?"
            rules={[{ required: true, message: 'Please explain why it’s not a good fit!' }]}
          >
            <Input.TextArea rows={3} placeholder="Type in here" />
          </Form.Item>
        )}

        <Form.Item
          name="email"
          label={
            <strong>
              {isPartiallyWorking
                ? `Conjuror currently does NOT generate TIPP&SEE draft for ${selectedModule} projects. Please enter your email address below if you would like to receive a generic TIPP&SEE template for this module.`
                : `Please enter your email address below if you would like to receive a TIPP&SEE draft for your customized ${selectedModule} project.`
              }
            </strong>
          }
          rules={[
            { type: 'email', message: 'Please enter a valid email address.' },
            { required: false }
          ]}
        >
          <Input
            onChange={(e) => setEmailEntered(!!e.target.value.trim())}
          />
        </Form.Item>

        {emailEntered && (
          <Form.Item
            name="confirmEmail"
            dependencies={['email']}
            rules={[
              { required: true, message: 'Please confirm your email address.' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('email') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two email addresses do not match.'));
                },
              }),
            ]}
          >
            <Input placeholder="Please re-enter your email address to confirm" />
          </Form.Item>
        )}

        {/* Submit Button */}

        <Form.Item>
          <div className="text-center">
            <Button type="primary" className="w-50" htmlType="submit" block>
              Download Project
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default Survey;
